/* iter55f75 — Novel Framework attribute schema + help copy.
 *
 * Single source of truth for every user-facing attribute in the
 * Framework Builder. Powers three surfaces:
 *
 *   1. The "Help" dialog (opened from File → Help) — lists every
 *      attribute, what it means, and what values it can take.
 *   2. The "Attributes" wizard section — richer dropdowns/toggles
 *      that a writer picks from without free-typing.
 *   3. Downstream export/import — same names as the backend model,
 *      so this file doubles as the format spec for template files.
 */

export const ATTRIBUTE_SCHEMA = [
  {
    group: 'Basics',
    attributes: [
      { key: 'name',          label: 'Working title',
        help: 'The novel’s current title. Change it any time — the manuscript title is decoupled from the file title.' },
      { key: 'genre',         label: 'Genre',
        help: 'Free-text genre label (literary, thriller, romance, sci-fi). Sets Claude’s baseline register.' },
      { key: 'logline',       label: 'Logline',
        help: 'One sentence: protagonist + obstacle + stakes. Everything else in the framework hangs from this pitch.' },
      { key: 'target_words',  label: 'Target word count',
        help: 'Range 10k–250k. Divides evenly across chapters to set each chapter’s draft length.' },
      { key: 'chapter_count', label: 'Chapter count',
        help: 'Range 5–40. Resizing preserves existing chapter data for the overlapping range.' },
    ],
  },
  {
    group: 'Story Bible',
    attributes: [
      { key: 'bible.pov',     label: 'Point of view',
        options: ['first', 'third-limited', 'third-omniscient', 'second'],
        help: 'Narrative perspective. `third-limited` (the default) is the most common contemporary choice; `first` reads more intimate; `omniscient` gives Claude license to head-hop; `second` is rare and intense.' },
      { key: 'bible.tense',   label: 'Tense',
        options: ['past', 'present'],
        help: '`past` is the default literary register. `present` reads more immediate but can wear thin over 80k words.' },
      { key: 'bible.voice',   label: 'Voice',
        options: ['literary', 'genre', 'YA', 'commercial', 'poetic', 'stark'],
        help: 'Overall register. `literary` = careful, sentence-level attention. `genre` = plot-forward pacing. `poetic` = image-driven, associative prose.' },
      { key: 'bible.world',   label: 'World / setting',
        help: 'A few sentences on the place and period. Claude reads this verbatim when drafting.' },
      { key: 'bible.themes',  label: 'Themes',
        help: 'What the book is *about* under the plot. Grief. Inheritance. What we owe the dead.' },
      { key: 'bible.tone',    label: 'Tone',
        help: 'Adjectives, not sentences. `melancholic, tightening into dread.` `dry, tender, funny.`' },
      { key: 'bible.imported_notes', label: 'Imported notes',
        help: 'Free-form paste-in — character sheets, worldbuilding, timeline notes. Claude reads this alongside the structured fields.' },
    ],
  },
  {
    group: 'Toggles',
    attributes: [
      { key: 'toggles.include_prologue',    label: 'Include a prologue',
        options: [true, false],
        help: 'When true, Claude may draft a chapter-0 prologue separate from the main arc.' },
      { key: 'toggles.include_epilogue',    label: 'Include an epilogue',
        options: [true, false],
        help: 'When true, the last chapter is treated as a coda rather than the climax.' },
      { key: 'toggles.include_the_end',     label: 'Mark the last chapter as “The End”',
        options: [true, false],
        help: 'Flips the last chapter’s `is_ending` flag once imported so the wordcount service awards the finished-novel badge even under 80k words.' },
      { key: 'toggles.dark_themes',         label: 'Dark themes',
        options: [true, false],
        help: 'Signals to Claude that violence, trauma, or unflinching material is on the table.' },
      { key: 'toggles.romance_thread',      label: 'Romantic subplot',
        options: [true, false],
        help: 'Adds a romance beat-line under the primary arc.' },
      { key: 'toggles.dual_timeline',       label: 'Dual timeline',
        options: [true, false],
        help: 'Split the manuscript across two time periods (past/present). Claude will alternate chapters unless you specify otherwise per-chapter.' },
      { key: 'toggles.unreliable_narrator', label: 'Unreliable narrator',
        options: [true, false],
        help: 'Tells Claude to seed the prose with inconsistencies the reader is meant to notice later.' },
    ],
  },
  {
    group: 'Character',
    attributes: [
      { key: 'characters[].name',        label: 'Name',
        help: 'The character’s primary name as it appears in the manuscript.' },
      { key: 'characters[].role',        label: 'Role',
        options: ['Protagonist', 'Antagonist', 'Mentor', 'Foil', 'Love Interest', 'Sidekick', 'Supporting'],
        help: 'The character’s structural function. Every framework should have at least one Protagonist and one Antagonist.' },
      { key: 'characters[].want',        label: 'Want',
        help: 'What the character consciously pursues. External. Concrete. In one sentence.' },
      { key: 'characters[].need',        label: 'Need',
        help: 'What the character actually requires to change — usually the opposite of their Want.' },
      { key: 'characters[].flaw',        label: 'Flaw',
        help: 'The wound / illusion / defence mechanism keeping them from their Need.' },
      { key: 'characters[].arc',         label: 'Arc',
        help: 'One-line trajectory: `hardening → cracking → tender`. How they change from beginning to end.' },
      { key: 'characters[].description', label: 'Description',
        help: 'Physical + voice notes. Age, hands, gait, speech tic. Claude weaves these into scenes.' },
    ],
  },
  {
    group: 'Chapter',
    attributes: [
      { key: 'chapters[].number',        label: 'Number',
        help: 'Chapter position 1..N. Auto-managed by the wizard — don’t edit by hand.' },
      { key: 'chapters[].title',         label: 'Title',
        help: 'The chapter title, e.g. `The Return`. Optional but strongly recommended.' },
      { key: 'chapters[].transition',    label: 'Transition (150–500 words)',
        help: 'A dense scene brief. Where the chapter opens, what the POV character wants in this scene, what escalates, what changes by the end. Claude uses this as the drafting prompt.' },
      { key: 'chapters[].pov_character', label: 'POV character',
        help: 'ID of one of your framework characters. Sets whose head we live in for this chapter.' },
      { key: 'chapters[].setting',       label: 'Setting',
        help: 'Where the chapter takes place (e.g. `the harbour at dusk`).' },
      { key: 'chapters[].conflict',      label: 'Conflict',
        help: 'The chapter’s central tension.' },
      { key: 'chapters[].revelation',    label: 'Revelation',
        help: 'The beat / reveal that closes the chapter and pulls the reader into the next.' },
      { key: 'chapters[].include_the_end', label: '“The End” marker',
        options: [true, false],
        help: 'When true on the last chapter, marks the manuscript as finished on import.' },
    ],
  },
];

/* Format spec (v1) — passed to Claude verbatim as documentation, and
 * also rendered inside the Help dialog under "How Claude reads this". */
export const FORMAT_SPEC_V1 = `A Novel Framework template is a JSON document with this shape:

{
  "format_version": 1,
  "kind":           "novel-framework",
  "generated_at":   "ISO-8601 timestamp",
  "framework": {
    "name":         "Working title",
    "genre":        "literary fiction | thriller | ...",
    "logline":      "One-sentence pitch",
    "target_words": 80000,
    "chapter_count": 12,
    "structure":    "3-act",
    "bible": {
      "pov":            "first | third-limited | third-omniscient | second",
      "tense":          "past | present",
      "voice":          "literary | genre | YA | commercial | poetic | stark",
      "world":          "A few sentences on place and period",
      "themes":         "Comma-separated abstract nouns",
      "tone":           "Adjectives, not sentences",
      "imported_notes": "Free-form paste-in for any prior bible / outline"
    },
    "toggles": {
      "include_prologue":    boolean,
      "include_epilogue":    boolean,
      "include_the_end":     boolean,
      "dark_themes":         boolean,
      "romance_thread":      boolean,
      "dual_timeline":       boolean,
      "unreliable_narrator": boolean
    },
    "characters": [
      { "name": "...", "role": "Protagonist", "want": "...",
        "need": "...", "flaw": "...", "arc": "...", "description": "..." }
    ],
    "chapters": [
      { "number": 1, "title": "...", "transition": "150–500 words",
        "pov_character": "chr_<id>", "setting": "...", "conflict": "...",
        "revelation": "...", "include_the_end": false }
    ]
  }
}

When The Loom imports a template it:
  1. Validates the shape via the pydantic FrameworkImport schema.
  2. Backfills any missing chapter numbers so 1..chapter_count is dense.
  3. Creates a fresh framework id, timestamps, and user linkage.
  4. Leaves imported_project_id unset — you can then Import to The Loom to materialise the manuscript.

Claude reads every field of every character and chapter when drafting a chapter, so the more you fill in, the more coherent the draft.`;
